import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, X, ArrowRight } from 'lucide-react';
import useCartStore from '../store/cartStore';
import { formatEUR } from '../utils/format';

/**
 * CartReminderBanner — In-App-Erinnerung.
 *
 * DSGVO: Diese Komponente ist **rein clientseitig** und braucht keine
 * Einwilligung — sie zeigt nur an, was eh schon im (lokalen) Warenkorb
 * des Nutzers steht.
 *
 * Logik:
 * - Anzeige nur, wenn ≥1 Item im Cart UND der Cart NICHT auf /cart oder
 *   /checkout sichtbar ist (sonst nervt es).
 * - Pro Session nur einmal anzeigen (sessionStorage merkt sich Dismiss).
 * - Erst nach 8 s nach Seitenaufruf einblenden (nicht aufdringlich).
 */
const DISMISS_KEY = 'hys-cart-reminder-dismissed';
const SHOW_DELAY_MS = 8000;

const CartReminderBanner = () => {
  const items = useCartStore((s) => s.items);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show on cart/checkout pages
    const path = window.location.pathname || '';
    if (path.startsWith('/cart') || path.startsWith('/checkout')) return;
    if (!items || items.length === 0) return;

    let dismissed = false;
    try { dismissed = sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { /* noop */ }
    if (dismissed) return;

    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [items]);

  // Hide also when items become empty
  useEffect(() => {
    if (!items || items.length === 0) setVisible(false);
  }, [items]);

  const dismiss = () => {
    setVisible(false);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* noop */ }
  };

  if (!visible) return null;

  const count = items.reduce((sum, it) => sum + (it.quantity || 1), 0);
  const subtotal = items.reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1), 0);

  return (
    <div
      className="fixed bottom-4 right-4 z-40 max-w-[22rem] bg-white border border-[#C4A084] shadow-xl p-4 animate-in slide-in-from-bottom-4"
      data-testid="cart-reminder-banner"
      role="dialog"
      aria-label="Warenkorb-Erinnerung"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-[#FAF5EE] border border-[#E8DCC8] flex items-center justify-center">
          <ShoppingCart className="h-5 w-5 text-[#C4A084]" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-semibold text-[#5D4E3C] leading-tight"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Du hast noch was im Warenkorb 💛
          </p>
          <p className="text-xs text-[#6D5A47] mt-0.5">
            <strong>{count}</strong> {count === 1 ? 'Artikel' : 'Artikel'} ·{' '}
            {formatEUR(subtotal)}
          </p>
          <div className="flex gap-2 mt-3">
            <Link
              to="/cart"
              onClick={dismiss}
              className="inline-flex items-center gap-1 bg-[#5D4E3C] hover:bg-[#4A3E30] text-white text-xs px-3 py-1.5 tracking-wider uppercase transition-colors"
              data-testid="cart-reminder-go-to-cart"
            >
              Zum Warenkorb
              <ArrowRight className="h-3 w-3" />
            </Link>
            <button
              onClick={dismiss}
              className="text-xs text-[#9C8C7A] hover:text-[#5D4E3C] tracking-wider uppercase"
              data-testid="cart-reminder-dismiss"
            >
              Später
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-[#9C8C7A] hover:text-[#5D4E3C] -mr-1 -mt-1"
          aria-label="Schließen"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default CartReminderBanner;
